import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, X, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import HeaderBar from '@/components/HeaderBar';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
    { value: 'viewer', label: 'Viewer', description: 'Can view analytics only' },
    { value: 'billing', label: 'Billing', description: 'Can manage billing and view analytics' },
    { value: 'editor', label: 'Editor', description: 'Can edit settings and view analytics' },
    { value: 'admin', label: 'Admin', description: 'Full access except team deletion' },
    { value: 'owner', label: 'Owner', description: 'Full access to everything' }
];

export default function TeamSetupPage({ token, user, logout }) {
    const navigate = useNavigate();
    const [teamName, setTeamName] = useState(`${user?.name || 'My'}'s Team`);
    const [members, setMembers] = useState([]);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [newMemberRole, setNewMemberRole] = useState('viewer');
    const [creating, setCreating] = useState(false);

    const addMember = () => {
        if (!newMemberEmail) {
            toast.error('Please enter an email address');
            return;
        }
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newMemberEmail)) {
            toast.error('Please enter a valid email address');
            return;
        }

        // Check if email already added
        if (members.some(m => m.email.toLowerCase() === newMemberEmail.toLowerCase())) {
            toast.error('This email has already been added');
            return;
        }

        // Check if trying to add own email
        if (user?.email?.toLowerCase() === newMemberEmail.toLowerCase()) {
            toast.error('You cannot invite yourself');
            return;
        }

        setMembers([...members, { email: newMemberEmail, role: newMemberRole }]);
        setNewMemberEmail('');
        setNewMemberRole('viewer');
    };

    const removeMember = (index) => {
        setMembers(members.filter((_, i) => i !== index));
    };

    const updateMemberRole = (index, role) => {
        const updated = [...members];
        updated[index].role = role;
        setMembers(updated);
    };

    const handleCreateTeam = async () => {
        if (!teamName.trim()) {
            toast.error('Please enter a team name');
            return;
        }

        setCreating(true);
        try {
            const response = await axios.post(
                `${API}/api/teams`,
                {
                    name: teamName,
                    members: members
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            toast.success('Team created successfully!');
            
            if (response.data.invited_members?.length > 0) {
                toast.info(`Invitations sent to ${response.data.invited_members.length} member(s)`);
            }

            navigate('/account');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create team');
        } finally {
            setCreating(false);
        }
    };

    const getRoleLabel = (roleValue) => {
        const role = ROLES.find(r => r.value === roleValue);
        return role ? role.label : roleValue;
    };

    return (
        <div className="settings-page">
            {/* Header Bar */}
            <HeaderBar user={user} logout={logout} />

            {/* Settings Header */}
            <div className="settings-header">
                <div className="settings-header-content settings-header-row">
                    <h1 className="settings-title">Create team</h1>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/account')}
                        className="back-button"
                    >
                        ← Back to settings
                    </Button>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-3xl mx-auto px-4 py-8">
                {/* Title */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-indigo-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Create a new team
                    </h1>
                    <p className="text-gray-600 max-w-md mx-auto">
                        Name your team, add team members and assign roles. When ready, click 'Create Team' to send invitations.
                    </p>
                </div>

                {/* Team Name Section */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Team name
                    </label>
                    <Input
                        type="text"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="Enter team name"
                        className="max-w-md"
                    />
                </div>

                {/* Team Members Section */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Team members</h2>
                    
                    {/* Add Member Form */}
                    <div className="flex gap-3 mb-6">
                        <div className="flex-1">
                            <Input
                                type="email"
                                value={newMemberEmail}
                                onChange={(e) => setNewMemberEmail(e.target.value)}
                                placeholder="Enter email address"
                                onKeyDown={(e) => e.key === 'Enter' && addMember()}
                            />
                        </div>
                        <select
                            value={newMemberRole}
                            onChange={(e) => setNewMemberRole(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            {ROLES.filter(r => r.value !== 'owner').map(role => (
                                <option key={role.value} value={role.value}>
                                    {role.label}
                                </option>
                            ))}
                        </select>
                        <Button onClick={addMember} variant="outline" className="flex items-center gap-1">
                            <Plus className="w-4 h-4" />
                            Invite
                        </Button>
                    </div>

                    {/* Current Members List */}
                    <div className="space-y-3">
                        {/* Owner (current user) */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-medium">
                                    {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900">{user?.name || user?.email}</div>
                                    <div className="text-sm text-gray-500">{user?.email}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                                    Owner
                                </span>
                            </div>
                        </div>

                        {/* Invited Members */}
                        {members.map((member, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 font-medium">
                                        {member.email.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900">{member.email}</div>
                                        <div className="text-sm text-yellow-600">Pending invitation</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={member.role}
                                        onChange={(e) => updateMemberRole(index, e.target.value)}
                                        className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        {ROLES.filter(r => r.value !== 'owner').map(role => (
                                            <option key={role.value} value={role.value}>
                                                {role.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => removeMember(index)}
                                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {members.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                            No team members added yet. Enter an email above to invite someone.
                        </p>
                    )}
                </div>

                {/* Role Descriptions */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Role permissions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {ROLES.map(role => (
                            <div key={role.value} className="flex items-start gap-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    role.value === 'owner' ? 'bg-indigo-100 text-indigo-700' :
                                    role.value === 'admin' ? 'bg-purple-100 text-purple-700' :
                                    role.value === 'editor' ? 'bg-blue-100 text-blue-700' :
                                    role.value === 'billing' ? 'bg-green-100 text-green-700' :
                                    'bg-gray-100 text-gray-700'
                                }`}>
                                    {role.label}
                                </span>
                                <span className="text-gray-600">{role.description}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Create Button */}
                <div className="flex justify-end">
                    <Button
                        onClick={handleCreateTeam}
                        disabled={creating}
                        className="bg-indigo-600 hover:bg-indigo-700 px-6"
                    >
                        {creating ? 'Creating...' : 'Create Team'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
